import React, { useEffect, useRef } from 'react';
import { dibujarMapaDelito, FOCOS_RESUMEN, BARRIOS } from './mapaDelitoDemo';

/**
 * Propuesta comercial de Adbize para Federico Chouhy.
 *
 * Página PÚBLICA (fuera de ProtectedRoute, igual que /en-accion): la idea es
 * poder mandarle el link sin que tenga que crear usuario ni loguearse.
 *
 * El estilo es propio y va scopeado bajo `.pa-root` — no usa los tokens de
 * GlobalStyles a propósito: la propuesta tiene identidad de documento
 * institucional (carátula de expediente, serif, ámbar/cian), distinta de la
 * del panel. Los tokens llevan prefijo `--pa-` para no chocar con los globales.
 *
 * Los dos acentos codifican la estructura de la propuesta:
 *   ámbar = lo que hacen personas de Adbize
 *   cian  = lo que hace sola la plataforma Arcana
 */

const CSS = `
.pa-root{
  --pa-ground:#0a121c;
  --pa-ground-2:#0c1622;
  --pa-panel:#101d2c;
  --pa-panel-2:#0d1826;
  --pa-edge:rgba(150,185,225,.16);
  --pa-edge-soft:rgba(150,185,225,.08);
  --pa-ink:#e9eff8;
  --pa-ink-2:#a6b6cd;
  --pa-ink-mute:#6b7c95;

  --pa-amber:#e9b452;
  --pa-amber-glow:rgba(233,180,82,.13);
  --pa-cyan:#4bbcd4;
  --pa-cyan-bright:#6fd0e4;
  --pa-cyan-glow:rgba(75,188,212,.13);
  --pa-ok:#43c98c;
  --pa-rule:rgba(150,185,225,.22);
  --pa-shadow:0 1px 0 rgba(255,255,255,.035) inset, 0 16px 38px -24px rgba(0,0,0,.85);

  --pa-serif:'Iowan Old Style','Palatino Linotype',Palatino,'Book Antiqua',Georgia,'Times New Roman',serif;
  --pa-sans:'Segoe UI',system-ui,-apple-system,'Helvetica Neue',Arial,sans-serif;
  --pa-mono:'JetBrains Mono','Cascadia Mono',Consolas,ui-monospace,monospace;

  min-height:100vh;
  background:var(--pa-ground);
  background-image:
    radial-gradient(900px 480px at 88% -6%, var(--pa-amber-glow), transparent 62%),
    radial-gradient(700px 420px at 4% 34%, var(--pa-cyan-glow), transparent 60%),
    linear-gradient(var(--pa-ground), var(--pa-ground-2));
  background-attachment:fixed;
  color:var(--pa-ink);
  font-family:var(--pa-sans);
  font-size:16px;
  line-height:1.65;
  -webkit-font-smoothing:antialiased;
}
.pa-root *{box-sizing:border-box}

.pa-page{max-width:1120px;margin:0 auto;padding:0 clamp(1rem,4vw,2.4rem)}
.pa-col{max-width:760px}
.pa-root main{padding-bottom:5rem}

.pa-root .eyebrow{font-family:var(--pa-mono);font-size:.66rem;letter-spacing:.22em;text-transform:uppercase;color:var(--pa-ink-mute);display:block}
.pa-root .eyebrow.a{color:var(--pa-amber)}
.pa-root .eyebrow.c{color:var(--pa-cyan)}

.pa-root h1,.pa-root h2,.pa-root h3{font-family:var(--pa-serif);font-weight:600;text-wrap:balance;letter-spacing:-.012em;margin:0}
.pa-root h1{font-size:clamp(2.05rem,5.4vw,3.35rem);line-height:1.08}
.pa-root h2{font-size:clamp(1.6rem,3.5vw,2.3rem);line-height:1.15}
.pa-root h3{font-size:1.16rem;line-height:1.3}
.pa-root p{margin:0}
.pa-root .lede{font-size:1.1rem;color:var(--pa-ink-2);text-wrap:pretty}
.pa-root strong{font-weight:600;color:var(--pa-ink)}
.pa-root em{font-style:normal;color:var(--pa-amber);font-weight:600}
.pa-root .num{font-variant-numeric:tabular-nums}

.pa-root a{color:var(--pa-cyan);text-decoration:none;border-bottom:1px solid var(--pa-cyan-glow)}
.pa-root a:hover{border-bottom-color:currentColor}
.pa-root a:focus-visible{outline:2px solid var(--pa-cyan-bright);outline-offset:3px;border-radius:2px}

/* ---- carátula de expediente ---- */
.pa-caratula{
  margin:clamp(1.6rem,5vw,3rem) 0 0;
  border:1px solid var(--pa-edge);background:var(--pa-panel);
  box-shadow:var(--pa-shadow);border-radius:3px;overflow:hidden;position:relative;
}
.pa-caratula::after{
  content:"";position:absolute;inset:0;pointer-events:none;
  background-image:linear-gradient(var(--pa-edge-soft) 1px,transparent 1px),
                   linear-gradient(90deg,var(--pa-edge-soft) 1px,transparent 1px);
  background-size:30px 30px;
  -webkit-mask-image:radial-gradient(520px 260px at 92% -12%,#000,transparent 72%);
  mask-image:radial-gradient(520px 260px at 92% -12%,#000,transparent 72%);
}
.pa-sello{
  display:flex;flex-wrap:wrap;align-items:center;gap:.5rem 1.4rem;
  border-bottom:1px solid var(--pa-edge);background:var(--pa-panel-2);
  padding:.7rem clamp(1.1rem,3vw,2rem);
  font-family:var(--pa-mono);font-size:.68rem;letter-spacing:.13em;text-transform:uppercase;color:var(--pa-ink-mute);
}
.pa-sello b{color:var(--pa-ink-2);font-weight:600;letter-spacing:.13em}
.pa-sello .marca{color:var(--pa-ink);display:flex;align-items:center;gap:.5rem;font-weight:700;letter-spacing:.16em}
.pa-sello .marca i{width:7px;height:7px;border-radius:50%;background:var(--pa-amber);box-shadow:0 0 0 3px var(--pa-amber-glow);font-style:normal;display:inline-block}
.pa-sello .sep{flex:1 1 auto}
.pa-caratula .cuerpo{padding:clamp(1.6rem,4.6vw,3rem) clamp(1.1rem,3vw,2rem) clamp(1.6rem,4vw,2.4rem);position:relative;z-index:1}
.pa-caratula h1 em{color:var(--pa-cyan);font-weight:600}
.pa-caratula .lede{margin-top:1.1rem;max-width:60ch}

/* Declaración de arranque: lo primero que tiene que quedar claro. */
.pa-declaracion{
  margin-top:1.4rem;border-left:2px solid var(--pa-amber);padding:.45rem 0 .45rem 1.05rem;
  font-size:1.05rem;line-height:1.55;color:var(--pa-ink);max-width:62ch;
}
.pa-declaracion b{color:var(--pa-amber);font-weight:600}

.pa-firma{display:flex;flex-wrap:wrap;gap:.55rem;margin-top:1.7rem}
.pa-chip{
  font-family:var(--pa-mono);font-size:.68rem;letter-spacing:.1em;text-transform:uppercase;
  border:1px solid var(--pa-edge);border-radius:2px;padding:.34rem .6rem;color:var(--pa-ink-2);background:var(--pa-panel-2);
}
.pa-chip.on{border-color:var(--pa-amber);color:var(--pa-amber)}
.pa-chip.ia{border-color:var(--pa-cyan);color:var(--pa-cyan)}

/* ---- tira de datos ---- */
.pa-tira{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(150px,100%),1fr));border:1px solid var(--pa-edge);border-top:0;background:var(--pa-panel-2)}
.pa-tira > div{padding:.95rem clamp(1.1rem,3vw,2rem);border-left:1px solid var(--pa-edge-soft)}
.pa-tira > div:first-child{border-left:0}
.pa-tira .v{font-family:var(--pa-serif);font-size:1.55rem;line-height:1.1;font-variant-numeric:tabular-nums;color:var(--pa-ink)}
.pa-tira .k{font-family:var(--pa-mono);font-size:.63rem;letter-spacing:.15em;text-transform:uppercase;color:var(--pa-ink-mute);margin-top:.3rem}

/* ---- secciones ---- */
.pa-root section{margin-top:clamp(3.4rem,8vw,5.6rem)}
.pa-cabecera{display:flex;flex-direction:column;gap:.6rem;max-width:70ch}
.pa-cabecera .regla{height:2px;width:56px;background:var(--pa-ink);margin-bottom:.2rem}
.pa-cabecera .regla.a{background:var(--pa-amber)}
.pa-cabecera .regla.c{background:var(--pa-cyan)}

.pa-bloque{
  margin-top:clamp(3.6rem,9vw,6rem);border-top:1px solid var(--pa-rule);padding-top:1.5rem;
  display:flex;align-items:baseline;gap:1rem;flex-wrap:wrap;
}
.pa-bloque .tag{
  font-family:var(--pa-mono);font-size:.68rem;letter-spacing:.2em;text-transform:uppercase;
  padding:.28rem .55rem;border-radius:2px;color:var(--pa-ground);background:var(--pa-amber);font-weight:700;
}
.pa-bloque .tag.c{background:var(--pa-cyan)}
.pa-bloque p{color:var(--pa-ink-2);font-size:.95rem;max-width:56ch}

/* ---- grillas ---- */
.pa-grid{display:grid;gap:1px;background:var(--pa-edge);border:1px solid var(--pa-edge);margin-top:1.8rem;border-radius:3px;overflow:hidden}
.pa-grid.dos{grid-template-columns:repeat(auto-fit,minmax(min(280px,100%),1fr))}
.pa-grid.tres{grid-template-columns:repeat(auto-fit,minmax(min(240px,100%),1fr))}
.pa-celda{background:var(--pa-panel);padding:1.5rem 1.4rem;display:flex;flex-direction:column;gap:.7rem}
.pa-celda h3{margin-top:.15rem}
.pa-celda p{font-size:.94rem;color:var(--pa-ink-2)}
.pa-celda ul{margin:.15rem 0 0;padding-left:1.05rem;font-size:.9rem;color:var(--pa-ink-2);display:flex;flex-direction:column;gap:.32rem}
.pa-celda li::marker{color:var(--pa-amber)}
.pa-celda.ia li::marker{color:var(--pa-cyan)}
.pa-celda .pie{margin-top:auto;padding-top:.9rem;border-top:1px dashed var(--pa-edge);font-family:var(--pa-mono);font-size:.66rem;letter-spacing:.1em;text-transform:uppercase;color:var(--pa-ink-mute)}

/* ---- tabla de accesos por persona ---- */
.pa-accesos-wrap{overflow-x:auto;margin-top:1.8rem;border:1px solid var(--pa-edge);border-radius:3px;background:var(--pa-panel)}
.pa-accesos{width:100%;border-collapse:collapse;min-width:660px}
.pa-accesos th{
  text-align:left;font-family:var(--pa-mono);font-size:.62rem;letter-spacing:.18em;text-transform:uppercase;
  color:var(--pa-ink-mute);font-weight:600;padding:.85rem 1.15rem;border-bottom:1px solid var(--pa-edge);background:var(--pa-panel-2);
}
.pa-accesos td{padding:1rem 1.15rem;border-bottom:1px solid var(--pa-edge-soft);vertical-align:top;font-size:.92rem;color:var(--pa-ink-2)}
.pa-accesos tr:last-child td{border-bottom:0}
.pa-accesos .quien{color:var(--pa-ink);font-weight:600;font-size:.97rem;line-height:1.35}
.pa-accesos .quien small{display:block;font-weight:400;font-size:.82rem;color:var(--pa-ink-mute);margin-top:.15rem}
.pa-accesos .rol{
  display:inline-block;font-family:var(--pa-mono);font-size:.63rem;letter-spacing:.12em;text-transform:uppercase;
  border:1px solid var(--pa-cyan);color:var(--pa-cyan);border-radius:2px;padding:.26rem .5rem;white-space:nowrap;
}
.pa-nota{
  margin-top:1rem;font-size:.89rem;color:var(--pa-ink-mute);
  border-left:2px solid var(--pa-edge);padding-left:.9rem;max-width:70ch;
}

/* ---- producto destacado (el tema que decide la eleccion) ---- */
.pa-destacado{
  margin-top:2rem;border:1px solid var(--pa-edge);border-radius:3px;background:var(--pa-panel);
  box-shadow:var(--pa-shadow);padding:clamp(1.7rem,4.5vw,2.8rem) clamp(1.2rem,3.5vw,2.4rem);
  position:relative;overflow:hidden;
}
.pa-destacado::before{content:"";position:absolute;left:0;right:0;top:0;height:3px;background:linear-gradient(90deg,var(--pa-amber),var(--pa-cyan))}
.pa-destacado .cabeza{display:flex;align-items:baseline;justify-content:space-between;gap:1rem;flex-wrap:wrap;margin-top:.3rem}
.pa-destacado .nombre{font-family:var(--pa-serif);font-size:clamp(1.5rem,3.4vw,2rem);line-height:1.15;color:var(--pa-ink)}
.pa-destacado .quien{font-family:var(--pa-mono);font-size:.65rem;letter-spacing:.14em;text-transform:uppercase;color:var(--pa-cyan);white-space:nowrap}
.pa-destacado .intro{margin-top:1rem;font-size:1.05rem;color:var(--pa-ink-2);max-width:68ch}

.pa-como{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(215px,100%),1fr));gap:1px;background:var(--pa-edge);
  border:1px solid var(--pa-edge);border-radius:3px;overflow:hidden;margin-top:1.7rem}
.pa-paso{background:var(--pa-panel-2);padding:1.15rem 1.2rem;display:flex;flex-direction:column;gap:.45rem}
.pa-paso .n{
  font-family:var(--pa-mono);font-size:.7rem;color:var(--pa-cyan);border:1px solid var(--pa-cyan);border-radius:2px;
  width:1.5rem;height:1.5rem;display:grid;place-items:center;font-variant-numeric:tabular-nums;
}
.pa-paso b{font-size:.95rem;font-weight:600;color:var(--pa-ink);line-height:1.35}
.pa-paso span{font-size:.87rem;color:var(--pa-ink-2);line-height:1.55}

/* ---- demo del mapa ---- */
.pa-demo{margin-top:1.7rem;border:1px solid var(--pa-edge);border-radius:3px;overflow:hidden;background:var(--pa-panel-2)}
.pa-demo .barra{
  display:flex;align-items:center;justify-content:space-between;gap:1rem;flex-wrap:wrap;
  padding:.7rem 1.1rem;border-bottom:1px solid var(--pa-edge);background:var(--pa-panel);
  font-family:var(--pa-mono);font-size:.63rem;letter-spacing:.15em;text-transform:uppercase;color:var(--pa-ink-mute);
}
.pa-demo .barra .vivo{display:flex;align-items:center;gap:.45rem;color:var(--pa-cyan)}
.pa-demo .barra .vivo i{width:6px;height:6px;border-radius:50%;background:var(--pa-cyan);font-style:normal;display:inline-block}
.pa-demo .lienzo{position:relative;display:block;width:100%;height:clamp(260px,46vw,420px);background:#0a121c}
.pa-demo canvas{display:block;width:100%;height:100%}
.pa-demo .patas{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(190px,100%),1fr));gap:1px;background:var(--pa-edge);border-top:1px solid var(--pa-edge)}
.pa-demo .pata{background:var(--pa-panel-2);padding:.85rem 1.1rem}
.pa-demo .pata .k{font-family:var(--pa-mono);font-size:.58rem;letter-spacing:.16em;text-transform:uppercase;color:var(--pa-ink-mute)}
.pa-demo .pata .v{font-family:var(--pa-serif);font-size:1.3rem;color:var(--pa-ink);font-variant-numeric:tabular-nums;margin-top:.15rem}
.pa-escala{display:flex;align-items:center;gap:.6rem;font-family:var(--pa-mono);font-size:.58rem;letter-spacing:.14em;text-transform:uppercase;color:var(--pa-ink-mute)}
.pa-escala .rampa{width:110px;height:7px;border-radius:2px;background:linear-gradient(90deg,#284a64,#50beC8,#e9b452,#eb4646)}

/* ---- aviso honesto sobre la demo ---- */
.pa-aviso{
  margin-top:1rem;border:1px dashed var(--pa-edge);border-radius:3px;padding:.9rem 1.1rem;
  font-size:.88rem;color:var(--pa-ink-2);background:var(--pa-panel-2);max-width:78ch;
}
.pa-aviso b{color:var(--pa-ink);font-weight:600}

/* ---- FAQ ---- */
.pa-faq{margin-top:1.8rem;border:1px solid var(--pa-edge);border-radius:3px;overflow:hidden;background:var(--pa-edge);display:grid;gap:1px}
.pa-faq details{background:var(--pa-panel)}
.pa-faq summary{
  cursor:pointer;padding:1.05rem 1.2rem;font-weight:600;color:var(--pa-ink);font-size:.99rem;
  display:flex;align-items:flex-start;gap:.7rem;list-style:none;
}
.pa-faq summary::-webkit-details-marker{display:none}
.pa-faq summary::before{
  content:"+";font-family:var(--pa-mono);color:var(--pa-cyan);font-size:1rem;line-height:1.4;flex:0 0 auto;
}
.pa-faq details[open] summary::before{content:"–"}
.pa-faq summary:hover{background:var(--pa-panel-2)}
.pa-faq summary:focus-visible{outline:2px solid var(--pa-cyan-bright);outline-offset:-2px}
.pa-faq .r{padding:0 1.2rem 1.15rem 2.6rem;font-size:.93rem;color:var(--pa-ink-2);max-width:72ch}

/* ---- piloto / garantías ---- */
.pa-garantias{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(250px,100%),1fr));gap:1px;background:var(--pa-edge);
  border:1px solid var(--pa-edge);border-radius:3px;overflow:hidden;margin-top:1.8rem}
.pa-garantia{background:var(--pa-panel);padding:1.35rem 1.35rem;display:flex;flex-direction:column;gap:.5rem}
.pa-garantia .tick{font-family:var(--pa-mono);font-size:.62rem;letter-spacing:.16em;text-transform:uppercase;color:var(--pa-ok)}
.pa-garantia b{font-size:1rem;color:var(--pa-ink);font-weight:600}
.pa-garantia span{font-size:.91rem;color:var(--pa-ink-2)}

/* ---- fugas (el costo de no hacer nada) ---- */
.pa-fugas{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(240px,100%),1fr));gap:1px;background:var(--pa-edge);
  border:1px solid var(--pa-edge);border-radius:3px;overflow:hidden;margin-top:1.8rem}
.pa-fuga{background:var(--pa-panel);padding:1.3rem 1.3rem;display:flex;flex-direction:column;gap:.45rem;position:relative}
.pa-fuga::before{content:"";position:absolute;left:0;top:0;bottom:0;width:2px;background:var(--pa-amber);opacity:.55}
.pa-fuga b{font-size:.98rem;color:var(--pa-ink);font-weight:600}
.pa-fuga span{font-size:.9rem;color:var(--pa-ink-2)}

/* ---- quiénes somos ---- */
.pa-nosotros{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(260px,100%),1fr));gap:1px;background:var(--pa-edge);
  border:1px solid var(--pa-edge);border-radius:3px;overflow:hidden;margin-top:1.8rem}
.pa-nosotros > div{background:var(--pa-panel);padding:1.35rem}
.pa-nosotros h3{font-size:1.02rem;margin-bottom:.45rem}
.pa-nosotros p{font-size:.92rem;color:var(--pa-ink-2)}

/* ---- bloque de marca ---- */
.pa-marca{
  margin-top:1.8rem;border:1px solid var(--pa-edge);border-radius:3px;background:var(--pa-panel);
  box-shadow:var(--pa-shadow);padding:clamp(1.4rem,3.5vw,2rem);
  display:flex;gap:clamp(1.2rem,3vw,2.2rem);align-items:flex-start;flex-wrap:wrap;
}
.pa-lockup{flex:0 0 auto;display:block}
.pa-lockup img{
  display:block;width:clamp(150px,22vw,210px);height:auto;
  /* el logo es multicolor sobre transparente: un halo suave lo despega del
     panel oscuro sin alterarle los colores de marca */
  filter:drop-shadow(0 2px 10px rgba(0,0,0,.45));
}
.pa-marca .datos{flex:1 1 300px;min-width:0}
.pa-marca .datos .tagline{font-size:1rem;color:var(--pa-ink-2);max-width:56ch}
.pa-marca .datos .donde{
  font-family:var(--pa-mono);font-size:.65rem;letter-spacing:.16em;text-transform:uppercase;
  color:var(--pa-amber);margin-top:.6rem;display:block;
}
.pa-enlaces{display:flex;flex-wrap:wrap;gap:.5rem;margin-top:1.1rem}
.pa-enlaces a{
  font-family:var(--pa-mono);font-size:.68rem;letter-spacing:.1em;text-transform:uppercase;
  border:1px solid var(--pa-edge);border-radius:2px;padding:.42rem .7rem;color:var(--pa-ink-2);
  background:var(--pa-panel-2);transition:.15s;
}
.pa-enlaces a:hover{border-color:var(--pa-cyan);color:var(--pa-cyan);background:var(--pa-cyan-glow)}

/* ---- familias de producto ---- */
.pa-familia{margin-top:clamp(2.6rem,6vw,3.6rem);display:flex;flex-direction:column;gap:.5rem;max-width:72ch}
.pa-familia .t{font-family:var(--pa-serif);font-size:1.42rem;line-height:1.25;color:var(--pa-ink)}
.pa-familia .d{font-size:.95rem;color:var(--pa-ink-2)}
.pa-familia .cuenta{font-family:var(--pa-mono);font-size:.62rem;letter-spacing:.16em;text-transform:uppercase;color:var(--pa-ink-mute)}

/* ---- fichas de producto ---- */
.pa-fichas{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(330px,100%),1fr));gap:1rem;margin-top:1.4rem;align-items:start}
.pa-ficha{background:var(--pa-panel);border:1px solid var(--pa-edge);border-radius:3px;padding:1.7rem clamp(1.2rem,3vw,1.9rem);display:grid;gap:.9rem;grid-template-columns:minmax(0,1fr)}
.pa-ficha .top{display:flex;align-items:baseline;justify-content:space-between;gap:1rem;flex-wrap:wrap}
.pa-ficha .nombre{font-family:var(--pa-serif);font-size:1.35rem;line-height:1.2;color:var(--pa-ink)}
.pa-ficha .quien{font-family:var(--pa-mono);font-size:.65rem;letter-spacing:.14em;text-transform:uppercase;color:var(--pa-cyan);white-space:nowrap}
.pa-ficha p{font-size:.97rem;color:var(--pa-ink-2);max-width:66ch}
.pa-ficha .porque{border-left:2px solid var(--pa-amber);padding:.15rem 0 .15rem .9rem;font-size:.92rem;color:var(--pa-ink-2)}
.pa-ficha .porque b{font-family:var(--pa-mono);font-size:.63rem;letter-spacing:.16em;text-transform:uppercase;color:var(--pa-amber);display:block;margin-bottom:.2rem}

/* ---- ideas ---- */
.pa-ideas{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(230px,100%),1fr));gap:1px;background:var(--pa-edge);border:1px solid var(--pa-edge);border-radius:3px;overflow:hidden;margin-top:1.6rem}
.pa-idea{background:var(--pa-panel-2);padding:1.1rem 1.2rem;display:flex;flex-direction:column;gap:.35rem}
.pa-idea b{font-size:.97rem;font-weight:600;color:var(--pa-ink)}
.pa-idea span{font-size:.87rem;color:var(--pa-ink-2);line-height:1.5}

/* ---- etapas (secuencia real: por eso van numeradas) ---- */
.pa-etapas{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(250px,100%),1fr));gap:1.6rem;margin-top:1.9rem}
.pa-etapa{display:flex;flex-direction:column;gap:.6rem;border-top:2px solid var(--pa-rule);padding-top:1rem}
.pa-etapa:first-child{border-top-color:var(--pa-amber)}
.pa-etapa .plazo{font-family:var(--pa-mono);font-size:.68rem;letter-spacing:.18em;text-transform:uppercase;color:var(--pa-ink-mute);display:flex;align-items:center;gap:.55rem}
.pa-etapa .plazo .n{font-size:.72rem;color:var(--pa-ink);border:1px solid var(--pa-edge);border-radius:2px;width:1.5rem;height:1.5rem;display:grid;place-items:center;letter-spacing:0;font-variant-numeric:tabular-nums}
.pa-etapa h3{font-size:1.05rem}
.pa-etapa p{font-size:.92rem;color:var(--pa-ink-2)}

/* ---- de tu lado ---- */
.pa-pedido{margin-top:1.7rem;border:1px solid var(--pa-edge);border-radius:3px;background:var(--pa-edge);display:grid;grid-template-columns:repeat(auto-fit,minmax(min(260px,100%),1fr));gap:1px;overflow:hidden}
.pa-pedido > div{background:var(--pa-panel);padding:1.3rem 1.35rem}
.pa-pedido h3{font-size:1rem;margin-bottom:.45rem}
.pa-pedido p{font-size:.91rem;color:var(--pa-ink-2)}
.pa-pedido .marca{font-family:var(--pa-mono);font-size:.63rem;letter-spacing:.16em;text-transform:uppercase;color:var(--pa-ok);display:block;margin-bottom:.5rem}

/* ---- cierre ---- */
.pa-cierre{
  margin-top:clamp(3.6rem,9vw,6rem);border:1px solid var(--pa-edge);border-radius:3px;background:var(--pa-panel);
  box-shadow:var(--pa-shadow);padding:clamp(1.9rem,5vw,3.2rem) clamp(1.2rem,4vw,3rem);position:relative;overflow:hidden;
}
.pa-cierre::before{content:"";position:absolute;left:0;right:0;top:0;height:3px;background:linear-gradient(90deg,var(--pa-amber),var(--pa-cyan))}
.pa-cierre h2{max-width:20ch}
.pa-cierre p{margin-top:1rem;max-width:60ch;color:var(--pa-ink-2);font-size:1.03rem}
.pa-cierre .pie{
  margin-top:2rem;padding-top:1.2rem;border-top:1px solid var(--pa-edge);
  display:flex;flex-wrap:wrap;gap:.6rem 2rem;align-items:center;
  font-family:var(--pa-mono);font-size:.7rem;letter-spacing:.13em;text-transform:uppercase;color:var(--pa-ink-mute);
}
.pa-cierre .pie b{color:var(--pa-ink);font-weight:700}

/* ---- contacto ---- */
.pa-contacto{margin-top:2.2rem;padding-top:1.5rem;border-top:1px solid var(--pa-edge);display:grid;grid-template-columns:repeat(auto-fit,minmax(min(200px,100%),1fr));gap:1.5rem 2rem}
.pa-contacto .item{display:flex;flex-direction:column;gap:.35rem;min-width:0}
.pa-contacto .k{font-family:var(--pa-mono);font-size:.62rem;letter-spacing:.18em;text-transform:uppercase;color:var(--pa-ink-mute)}
.pa-contacto .v{font-size:1rem;font-weight:600;color:var(--pa-ink);overflow-wrap:anywhere}
.pa-contacto .v a{color:var(--pa-ink);border-bottom:1px solid var(--pa-amber)}
.pa-contacto .v a:hover{color:var(--pa-amber)}
.pa-contacto .n{font-size:.86rem;color:var(--pa-ink-2);font-weight:400}

.pa-root footer{
  margin-top:3rem;padding-top:1.3rem;border-top:1px solid var(--pa-edge-soft);
  font-family:var(--pa-mono);font-size:.66rem;letter-spacing:.14em;text-transform:uppercase;color:var(--pa-ink-mute);
  display:flex;justify-content:space-between;gap:1rem;flex-wrap:wrap;
}

/* ---- revelado al hacer scroll ----
   Mejora progresiva: el contenido es visible por defecto. Recién cuando el
   JS confirma que puede animar agrega .pa-anim al contenedor y ahí sí se
   esconde para revelarse al scrollear. Si el JS falla, no carga o el
   observer nunca dispara, la propuesta se lee igual — nunca en blanco. */
.pa-anim .pa-reveal{opacity:0;transform:translateY(14px);transition:opacity .55s ease,transform .55s ease}
.pa-anim .pa-reveal.visto{opacity:1;transform:none}

@media (prefers-reduced-motion: reduce){
  .pa-root *{animation:none !important;transition:none !important}
  .pa-reveal{opacity:1 !important;transform:none !important}
}
/* ============================================================
   RESPONSIVE
   Las grillas ya se acomodan solas (auto-fit + min() para que
   nunca sean más anchas que la pantalla). Acá van los cambios
   que el reflow automático no resuelve.
   ============================================================ */

/* La tabla de accesos deja de ser tabla y pasa a fichas apiladas:
   en un teléfono, tres columnas obligan a scrollear de costado. */
@media (max-width:720px){
  .pa-accesos-wrap{overflow-x:visible}
  .pa-accesos{min-width:0}
  .pa-accesos thead{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
  .pa-accesos tr{display:block;border-bottom:1px solid var(--pa-edge);padding:.55rem 0}
  .pa-accesos tr:last-child{border-bottom:0}
  .pa-accesos td{display:block;border:0;padding:.4rem 1.15rem}
  .pa-accesos td::before{
    content:attr(data-label);display:block;font-family:var(--pa-mono);font-size:.57rem;
    letter-spacing:.18em;text-transform:uppercase;color:var(--pa-ink-mute);margin-bottom:.3rem;
  }
  .pa-accesos td:first-child::before{display:none}
}

@media (max-width:560px){
  .pa-root{font-size:15.5px}
  /* background-attachment:fixed pega saltos al scrollear en iOS */
  .pa-root{background-attachment:scroll}
  .pa-sello{gap:.35rem .9rem;font-size:.62rem}
  .pa-sello .sep{display:none}
  .pa-tira > div{border-left:0;border-top:1px solid var(--pa-edge-soft)}
  .pa-tira > div:first-child{border-top:0}
  /* el bloque y su bajada, uno abajo del otro en vez de pelear por el ancho */
  .pa-bloque{flex-direction:column;gap:.7rem}
  .pa-ficha .top{flex-direction:column;gap:.3rem}
  .pa-destacado .cabeza{flex-direction:column;gap:.35rem}
  .pa-cierre .pie{flex-direction:column;align-items:flex-start;gap:.5rem}
  .pa-root footer{flex-direction:column;gap:.5rem}
}
`;

/**
 * Accesos por persona. Los roles son los que existen de verdad en la
 * plataforma (api/_lib/auth.js → ROLES), no una lista de folleto:
 * admin · estratega · comunicador · analista · referente · visualizador.
 */
const ACCESOS = [
  {
    quien: 'Federico',
    sub: 'El titular de la cuenta',
    rol: 'Administrador',
    que: 'Todo, sin excepción. Es el único que da de alta y de baja a la gente del equipo, cambia los permisos de cada uno y toca lo sensible. Si alguien se va, lo saca él.',
  },
  {
    quien: 'Tu mano derecha',
    sub: 'Jefe de campaña o coordinador general',
    rol: 'Estratega',
    que: 'Todo lo que ve Federico salvo el alta y baja de usuarios: sala de estrategia, modo crisis, inteligencia, competencia, licitaciones y los perfiles digitales.',
  },
  {
    quien: 'Quien maneja las redes y la prensa',
    sub: 'Community, diseño, vocería',
    rol: 'Comunicador',
    que: 'Mensajería y materiales, motor de prensa, medios, reputación y redes. Prepara y publica el contenido. No entra a inteligencia ni a los perfiles digitales.',
  },
  {
    quien: 'Quien atiende a los vecinos',
    sub: 'Referentes de barrio, los que recorren',
    rol: 'Referente territorial',
    que: 'Mapa de barrios, recorridas y la base de vecinos con sus reclamos. Cargan desde el celular, en la esquina, sin abrir la computadora.',
  },
  {
    quien: 'Quien sigue los números y el Concejo',
    sub: 'Analista, asesor legislativo',
    rol: 'Analista',
    que: 'Lectura de toda la plataforma más investigación, dossiers, análisis, radar de licitaciones y el mapa electoral. Arma informes, no modifica la operación.',
  },
  {
    quien: 'Aliados y colaboradores',
    sub: 'Concejales amigos, quien solo necesita mirar',
    rol: 'Visualizador',
    que: 'Solo lectura de tableros y reportes. Ve cómo viene todo sin poder cambiar nada. Sirve para mostrar avance sin abrir la cocina.',
  },
];

/**
 * Cómo se arma el Mapa del Delito. Es un pipeline real (entra el dato →
 * se ubica → se agrupa → sale el mapa), por eso va numerado.
 */
const MAPA_PASOS = [
  {
    t: 'Lo que ya se dice en redes',
    d: 'La plataforma sigue los grupos públicos de vecinos, los portales locales y los comentarios. Cuando alguien cuenta un robo, la IA lee el posteo, saca qué pasó, cuándo y en qué esquina, y lo ubica en el mapa.',
  },
  {
    t: 'Lo que marca el vecino',
    d: 'Desde el celular, en veinte segundos y sin dar el nombre: qué pasó, dónde y a qué hora. Anónimo a propósito, porque la mayoría de los hechos nunca llega a la denuncia formal.',
  },
  {
    t: 'El mapa se pinta solo',
    d: 'Los hechos se agrupan por zona y por franja horaria, y salen los puntos de calor. Nadie carga nada a mano: se actualiza mientras entran los datos.',
  },
  {
    t: 'Y de ahí salen los pedidos',
    d: 'Ranking de las esquinas más calientes, evolución mes a mes y contraste con lo que dice el municipio. Cada punto rojo es un proyecto con respaldo.',
  },
];

/**
 * Los productos del bloque 2, agrupados por familia. La familia no es
 * decoración: cada una responde a un posicionamiento distinto (mostrar,
 * resolver, escuchar, organizar), y se pueden encarar en cualquier orden.
 */
const FAMILIAS = [
  {
    eyebrow: 'Familia 1 · Transparencia',
    titulo: 'El que abre la información',
    desc: 'Todo lo que hoy está cerrado en un PDF o en mesa de entradas, abierto y consultable. En una ciudad donde nadie muestra, el que muestra se queda con el tema.',
    productos: [
      {
        nombre: 'Concejo Abierto',
        quien: 'Público · con IA',
        texto: 'Una web donde cualquier vecino de San Nicolás le pregunta a una inteligencia artificial todo lo del Honorable Concejo Deliberante y le responde en castellano, al toque: qué ordenanzas hay sobre un tema, qué se trató en la última sesión, en qué estado está un expediente. Sin PDF, sin buscar, sin ir a mesa de entradas.',
        porque: 'El Concejo es percibido como una caja cerrada. El que lo abre se queda con la bandera de la transparencia durante todo el mandato. Además, cada consulta te dice qué le preocupa a la gente en tiempo real, mejor que cualquier encuesta.',
      },
      {
        nombre: 'Presupuesto Abierto',
        quien: 'Público · con IA',
        texto: 'Cuánta plata entra al municipio, en qué se gasta y qué le tocó a cada área, en gráficos que se entienden sin saber de contabilidad. El vecino pregunta “cuánto se gastó en luminarias este año” y recibe el número con la fuente al lado.',
        porque: 'Es la pregunta más incómoda para cualquier intendente. Tener el dato antes que él, y publicado, te pone a vos en control de la discusión sobre la plata.',
      },
      {
        nombre: 'Obra en Marcha',
        quien: 'Público · mapa',
        texto: 'El mapa de las obras del municipio: cuál se licitó, a quién se le adjudicó, cuánto salió, cuándo tenía que estar terminada y cómo viene. Se alimenta del radar de licitaciones que ya corre adentro de la plataforma.',
        porque: 'Convierte “no hacen nada” en un dato verificable con fecha y monto. Y cuando una obra se atrasa, el que avisa primero sos vos.',
      },
      {
        nombre: 'Voto por Voto',
        quien: 'Público · registro',
        texto: 'Cómo votó cada concejal, sesión por sesión y tema por tema, en una pantalla que se entiende de un vistazo. Con buscador por nombre y por tema, y el texto de lo que se votó a un clic.',
        porque: 'Deja registro público de las posiciones de todos, incluida la tuya. Llegada la campaña, nadie puede reescribir lo que hizo.',
      },
    ],
  },
  {
    eyebrow: 'Familia 2 · Servicio',
    titulo: 'El que resuelve',
    desc: 'Herramientas que le sirven al vecino todos los días, tenga o no interés en política. Se usan porque son útiles — y de paso construyen la relación.',
    productos: [
      {
        nombre: 'Mi Barrio',
        quien: 'Público · reclamos',
        texto: 'La plataforma de reclamos de barrio, por WhatsApp y web. El vecino manda una foto del pozo, el árbol caído o la luminaria apagada, queda ubicada en el mapa automáticamente, entra al sistema con número de seguimiento y recibe aviso cuando el reclamo avanza. Del otro lado, tu equipo ve el mapa de calor: qué barrio, qué problema, cuánto hace que espera.',
        porque: 'Convierte una queja suelta en un contacto con nombre, teléfono y motivo. En dos años son miles de vecinos que te deben una respuesta concreta — la base de datos más valiosa que puede tener una campaña, construida haciendo bien el trabajo.',
      },
      {
        nombre: 'Alerta San Nicolás',
        quien: 'Público · avisos',
        texto: 'El vecino se suscribe a su barrio y recibe solo lo que le afecta: corte de agua o de luz, calle cortada, alerta de temporal, operativo de seguridad, cambio en el recorrido del camión. Por WhatsApp, sin instalar nada.',
        porque: 'Es el servicio que hace que te tengan agendado. Cuando llega la campaña ya tenés un canal directo con miles de vecinos que ellos mismos pidieron abrir.',
      },
      {
        nombre: 'Mesa de Entrada Digital',
        quien: 'Público · seguimiento',
        texto: 'Cada gestión que hacés por un vecino recibe un número y una página propia donde él mismo ve en qué anda: quién la tomó, qué se pidió, qué contestó el municipio y en qué fecha.',
        porque: 'Casi todo el trabajo de un concejal se pierde porque el vecino nunca se entera de que se hizo. Esto lo vuelve visible sin que tengas que salir a contarlo.',
      },
      {
        nombre: 'Agenda Abierta',
        quien: 'Público · agenda',
        texto: 'Dónde va a estar Federico esta semana: qué barrio recorre, en qué reunión está, cuándo sesiona el Concejo. Publicada antes de que pase, no después.',
        porque: 'Es la forma más barata de ser accesible y de que se note. Además le da al vecino un motivo concreto y un lugar para ir a buscarte.',
      },
    ],
  },
  {
    eyebrow: 'Familia 3 · Conversación',
    titulo: 'El que escucha y responde',
    desc: 'Que hablar con Federico sea posible a cualquier hora, y que lo que la gente dice vuelva convertido en decisiones.',
    productos: [
      {
        nombre: 'Federico Responde',
        quien: 'Público · asistente IA',
        texto: 'Un asistente con IA, en tu web y en WhatsApp, para cualquiera que quiera saber de vos: qué proponés sobre seguridad, qué votaste el mes pasado, qué hiciste por su barrio, cómo pedirte una reunión. Responde con tus posiciones reales, con tu tono, y cuando no sabe algo lo deriva a una persona del equipo en lugar de inventar.',
        porque: 'Contesta a las tres de la mañana, no se cansa y nunca contradice la línea. Cada conversación queda registrada: sabés exactamente qué le están preguntando al candidato y qué temas hay que empezar a trabajar.',
      },
      {
        nombre: 'Consulta Vecinal',
        quien: 'Público · participación',
        texto: 'Votaciones cortas por barrio: cuál de estas tres obras querés primero, qué es lo más urgente de tu cuadra. El resultado se publica y se lleva al Concejo como proyecto.',
        porque: 'Te da mandato real para presentar — “me lo pidieron cuatrocientos vecinos de Santa Rosa” — y mide prioridades mejor y más barato que una encuesta paga.',
      },
      {
        nombre: 'Banco de Testimonios',
        quien: 'Interno · material',
        texto: 'Los vecinos cuya gestión se resolvió cuentan qué pasó, en video corto o por escrito. Queda ordenado por barrio y por tema, listo para usar cuando haga falta.',
        porque: 'En campaña, un vecino diciendo “me resolvió el problema” vale más que cualquier spot producido. Se junta ahora, sin costo, mientras se trabaja.',
      },
      {
        nombre: 'Verificador',
        quien: 'Público · con IA',
        texto: 'Una página donde el vecino pega la cadena de WhatsApp que le llegó y le dice qué hay de cierto, con las fuentes. Empieza por los temas de San Nicolás y por lo que se dice de Federico.',
        porque: 'Es una herramienta de defensa que no parece defensa. Y te avisa qué operación está circulando mientras todavía es chica.',
      },
    ],
  },
  {
    eyebrow: 'Familia 4 · Estructura',
    titulo: 'La máquina de la elección',
    desc: 'Esto ya no es comunicación: es lo que decide una elección ajustada. Se construye con dos años de anticipación, no en los últimos dos meses.',
    productos: [
      {
        nombre: 'Fiscal Digital',
        quien: 'Interno · día de la elección',
        texto: 'La app de tus fiscales: quién está en qué escuela y en qué mesa, carga del resultado y foto del telegrama apenas cierra, y el conteo propio armándose en vivo mientras el escrutinio oficial todavía no salió.',
        porque: 'Es la diferencia entre saber el resultado antes que nadie y enterarte por la tele. Y en una elección ajustada, es también la prueba si hay que reclamar.',
      },
      {
        nombre: 'Puerta a Puerta',
        quien: 'Interno · territorio',
        texto: 'El timbreo ordenado: a quién visitar, qué se habló la vez anterior, qué contestó, todo cargado desde el celular en la misma puerta. Cada militante abre su recorrido del día ya armado.',
        porque: 'Convierte la caminata en datos que se acumulan. Al segundo año sabés, manzana por manzana, dónde estás fuerte y dónde hay que volver.',
      },
      {
        nombre: 'Central de Voluntarios',
        quien: 'Interno · estructura',
        texto: 'Alta de voluntarios desde la web, con qué puede aportar cada uno y en qué horarios. Se les asignan tareas concretas y queda registrado quién cumplió.',
        porque: 'El voluntario que se anota y nadie llama en dos semanas se pierde para siempre. Esto es lo único que evita esa fuga.',
      },
      {
        nombre: 'Sala de Situación Electoral',
        quien: 'Interno · día D',
        texto: 'El tablero del domingo: participación por circuito en vivo, alerta de mesas sin fiscal, resultados entrando y proyección propia a medida que cargan.',
        porque: 'Te deja decidir durante el día — dónde mandar gente a buscar votantes que no fueron — en lugar de esperar sentado el resultado.',
      },
      {
        nombre: 'Escuela de Referentes',
        quien: 'Interno · formación',
        texto: 'Formación para tus referentes de barrio: cómo tomar un reclamo, cómo hablar con un vecino difícil, qué puede y qué no puede prometer un concejal. Con seguimiento de quién la hizo.',
        porque: 'Una estructura territorial sin formación repite errores y promete lo que no se puede cumplir. Esto profesionaliza la gente que ya tenés.',
      },
    ],
  },
];

/** Lo que hoy se pierde por no tener el sistema. */
const FUGAS = [
  ['Los vecinos que te escriben y se pierden', 'Llegan por WhatsApp, por Instagram, por un conocido. Se resuelven o no, pero casi ninguno queda anotado. Al mes siguiente nadie se acuerda de quién era ni qué pidió.'],
  ['Los temas que explotan sin aviso', 'Una queja de barrio que crece tres días en un grupo de Facebook y llega a un portal. Cuando te enterás, ya hay que salir a apagar el incendio en vez de haberlo tomado como propio.'],
  ['El trabajo que hacés y no se ve', 'Cada proyecto presentado, cada gestión resuelta, cada recorrida. Si no sale publicado y el vecino no se entera, políticamente es como si no hubiera pasado.'],
  ['Las oportunidades que agarra otro', 'El tema que estaba dando vueltas y lo tomó otro concejal primero. No es que vos no lo hubieras visto: es que nadie tenía el trabajo de mirar todos los días.'],
];

/** Piloto y condiciones — bajar el riesgo de decir que sí. */
const GARANTIAS = [
  ['La plataforma se adapta a ustedes', 'Si algo no encaja con la forma en que trabaja tu equipo, se cambia la plataforma — no el equipo. Agregar un campo, cambiar un circuito o sumar una pantalla lo hacemos nosotros, sin costo extra y sin esperar la próxima versión de nadie.'],
  ['Piloto de 90 días, a costo', 'Durante los primeros 90 días solo cobramos lo que cuesta sostener el servicio: los servidores, las herramientas, los servicios de inteligencia artificial que consume la plataforma y las horas de los programadores. Sin margen de ganancia. Pasados los 90 días, si les sirvió, seguimos con el precio final; si no, se termina ahí, sin penalidad y sin discusión.'],
  ['Salís cuando quieras', 'No hay permanencia mínima después del piloto. Se avisa con 30 días y listo.'],
  ['Te llevás los datos y lo construido', 'Si te vas, exportamos todo y te lo entregamos: vecinos, reclamos, historial, contenidos — y también las herramientas que hayamos desarrollado para vos en ese tiempo. Lo único que no se va con vos es Arcana: la plataforma es nuestra y sigue siéndolo.'],
  ['Sin costo de instalación', 'La plataforma ya está construida y funcionando. No pagás el desarrollo de lo que ya existe.'],
];

/** Objeciones reales, contestadas antes de que las diga. */
const FAQ = [
  [
    '¿Esto no lo puede hacer alguien del equipo con ChatGPT?',
    'Una parte sí: redactar un texto, resumir una nota. Lo que no se puede hacer con ChatGPT es lo otro — mirar todos los días lo que se publica de vos, ordenar cada reclamo de vecino con su seguimiento, cruzar barrios con reclamos, y que todo eso quede guardado y comparable en el tiempo. Eso no es una consulta: es un sistema corriendo. Y sobre todo, alguien tiene que hacerlo todos los días a las siete de la mañana, no cuando se acuerda.',
  ],
  [
    '¿Me quedo atado a ustedes?',
    'No. Los primeros 90 días son a costo —se cubren los servidores, las herramientas y las horas de los programadores, sin margen de ganancia— y se sale sin penalidad. Después no hay permanencia, y si te vas te llevás la base completa exportada y las herramientas que hayamos construido para vos. La razón para seguir tiene que ser que funciona, no que no podés salir.',
  ],
  [
    '¿Cuánta gente de mi equipo tiene que aprender a usar esto?',
    'Los que ya trabajan con vos, y cada uno solo la parte suya. El que atiende vecinos usa el celular para cargar un reclamo — eso se aprende en cinco minutos. El resto entra a su sección y no ve nada más. No hay curso ni manual.',
  ],
  [
    '¿Qué pasa si se filtran los datos de los vecinos?',
    'Cada persona entra con su usuario y ve solo lo suyo; las secciones sensibles están cerradas por rol. Los datos viven en un servidor propio, no en una planilla que circula por WhatsApp — que es exactamente donde están hoy en la mayoría de los equipos. Si alguien deja el equipo, se le saca el acceso en el momento.',
  ],
  [
    '¿Y si el Mapa del Delito se llena de cargas falsas?',
    'Los reportes pasan por moderación antes de aparecer, se cruzan con lo que ya se detectó en redes, y se puede limitar la carga por dispositivo. Además el mapa aclara siempre que son reportes de vecinos y no estadística oficial: no se presenta como lo que no es.',
  ],
  [
    '¿Cuánto tardan en tener algo funcionando?',
    'La plataforma está andando hoy: el acceso se te da en la primera semana, con tus datos ya cargados. Y la primera herramienta pública sale entre el día 30 y el 45 — es rápido porque el sistema de atrás ya está construido y lo que se arma es solo la pieza que ve el vecino.',
  ],
  [
    '¿Esto reemplaza a la gente que tengo?',
    'No, y no nos conviene que lo parezca. Nosotros nos montamos sobre tu equipo: les sacamos el trabajo pesado y repetitivo para que se dediquen a lo que solo ellos pueden hacer, que es hablar con la gente.',
  ],
];

const IDEAS = [
  ['Panel de compromisos', 'Lo que prometiste, en verde o en rojo, actualizado por vos mismo. Se lo mostrás a la gente antes de que te lo reclamen.'],
  ['Encuestas relámpago', 'Consulta corta por WhatsApp a los vecinos de un barrio puntual. Respuesta en horas, no en semanas.'],
  ['Simulador de entrevistas', 'Entrenamiento con IA: te hace las preguntas más duras que te pueden hacer y te devuelve dónde te trabaste.'],
  ['Boletín por barrio', 'Cada vecino recibe novedades de su zona, no un mail genérico. Se arma solo con lo que ya está cargado.'],
  ['Ficha de vecino', 'Antes de una reunión, la pantalla te dice quién es, qué reclamó, cuándo hablaron y en qué quedaron.'],
  ['Video-respuestas semanales', 'Las preguntas más repetidas de la semana, contestadas por vos en un video corto. El contenido lo elige la gente.'],
];

export default function Propuesta() {
  const lienzo = useRef(null);

  useEffect(() => {
    document.title = 'Adbize para Chouhy · Propuesta';
  }, []);

  // El mapa se dibuja cuando entra en pantalla (no antes: es la pieza más
  // pesada de la página) y se redibuja si cambia el ancho.
  useEffect(() => {
    const canvas = lienzo.current;
    if (!canvas) return undefined;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let limpiar = () => {};
    let dibujado = false;

    const dibujar = (animar) => { limpiar(); limpiar = dibujarMapaDelito(canvas, { animar }); };

    // El mapa se dibuja SIEMPRE, aunque el observer nunca dispare: un recuadro
    // negro y vacío en el medio de la propuesta es peor que perderse la
    // animación. El observer solo decide si vale la pena animarlo.
    const obs = 'IntersectionObserver' in window && !reduce
      ? new IntersectionObserver((entradas) => {
        entradas.forEach((e) => {
          if (e.isIntersecting && !dibujado) { dibujado = true; dibujar(true); obs.disconnect(); }
        });
      }, { threshold: 0.15 })
      : null;

    if (obs) {
      obs.observe(canvas);
      // Si en 1,2 s no entró en pantalla (o el navegador no entrega el aviso),
      // se dibuja quieto y listo.
      setTimeout(() => { if (!dibujado) { dibujado = true; obs.disconnect(); dibujar(false); } }, 1200);
    } else {
      dibujado = true;
      dibujar(false);
    }

    let t = 0;
    const alRedimensionar = () => {
      clearTimeout(t);
      t = setTimeout(() => { if (dibujado) dibujar(false); }, 180);
    };
    window.addEventListener('resize', alRedimensionar);

    return () => {
      if (obs) obs.disconnect();
      window.removeEventListener('resize', alRedimensionar);
      clearTimeout(t);
      limpiar();
    };
  }, []);

  // Revelado suave al entrar en pantalla — como mejora, nunca como requisito.
  // El contenido arranca visible; solo si podemos animar agregamos .pa-anim.
  // Y aunque el observer no dispare (pestaña en segundo plano, navegador raro),
  // una red de seguridad muestra todo igual.
  useEffect(() => {
    const raiz = document.querySelector('.pa-root');
    const items = document.querySelectorAll('.pa-reveal');
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!raiz || reduce || !('IntersectionObserver' in window)) return undefined;

    const mostrarTodo = () => items.forEach((el) => el.classList.add('visto'));

    raiz.classList.add('pa-anim');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('visto'); obs.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    items.forEach((el) => obs.observe(el));

    // Red de seguridad: si en dos segundos no se revelo nada, se muestra todo.
    const red = setTimeout(() => {
      const alguno = document.querySelector('.pa-reveal.visto');
      if (!alguno) { obs.disconnect(); mostrarTodo(); }
    }, 2000);

    return () => { clearTimeout(red); obs.disconnect(); };
  }, []);

  return (
    <div className="pa-root">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      <div className="pa-page">
        <main>

          {/* ============ CARÁTULA ============ */}
          <header className="pa-caratula">
            <div className="pa-sello">
              <span className="marca"><i></i>Adbize</span>
              <span className="sep"></span>
              <span><b>Destinatario</b> Federico Chouhy</span>
              <span><b>Asunto</b> Propuesta de trabajo</span>
              <span><b>Plaza</b> San Nicolás</span>
            </div>

            <div className="cuerpo">
              <span className="eyebrow a">Adbize &nbsp;·&nbsp; con la plataforma Arcana</span>
              <h1 style={{ marginTop: '1.1rem' }}>
                Un equipo de campaña que trabaja todos los días, y una <em>máquina</em> que no se toma franco.
              </h1>
              <p className="lede">
                No te ofrecemos horas de agencia ni un paquete de posteos. Te ofrecemos la operación completa:
                la gente que hace prensa, pauta y contenido, montada sobre una plataforma propia que escucha,
                ordena y avisa sola. Una parte ya está construida y funcionando. La otra la construimos con vos.
              </p>

              <p className="pa-declaracion">
                Que quede claro desde el arranque: <b>no venimos a reemplazar a nadie de tu equipo</b>.
                Venimos a potenciar a la gente que ya tenés y a cubrir los huecos que hoy no llega a
                tapar nadie — que no es lo mismo que hacerlo mejor: es hacerlo posible.
              </p>

              <div className="pa-firma">
                <span className="pa-chip on">Prensa y vocería</span>
                <span className="pa-chip on">Pauta digital</span>
                <span className="pa-chip on">Territorio y reclamos</span>
                <span className="pa-chip ia">Plataforma Arcana</span>
                <span className="pa-chip ia">Productos con IA</span>
              </div>
            </div>
          </header>

          <div className="pa-tira">
            <div><div className="v num">2</div><div className="k">Municipios donde ya funciona</div></div>
            <div><div className="v num">8</div><div className="k">Equipos en la plataforma</div></div>
            <div><div className="v num">30+</div><div className="k">Herramientas ya funcionando</div></div>
            <div><div className="v num">24/7</div><div className="k">Escucha y alertas</div></div>
            <div><div className="v num">2027</div><div className="k">El horizonte</div></div>
          </div>

          {/* ============ PUNTO DE PARTIDA ============ */}
          <section className="pa-col pa-reveal">
            <div className="pa-cabecera">
              <div className="regla"></div>
              <span className="eyebrow">El punto de partida</span>
              <h2>El problema no es falta de trabajo. Es que el trabajo no se ve.</h2>
            </div>
            <p className="lede" style={{ marginTop: '1.2rem' }}>
              Un concejal en funciones produce todos los días: proyectos, recorridas, reuniones, respuestas a vecinos.
              Casi nada de eso llega afuera con la fuerza que tiene. Mientras tanto, la conversación de San Nicolás
              pasa en tres o cuatro grupos de WhatsApp, dos portales locales y un puñado de cuentas de Instagram,
              y nadie del equipo tiene tiempo de leer todo eso a las siete de la mañana.
            </p>
            <p className="lede" style={{ marginTop: '1rem' }}>
              Nuestro trabajo tiene dos mitades. Una es de oficio: prensa, pauta, contenido, calle.
              La otra es de máquina: que la plataforma lea, ordene y te avise antes de que el tema explote.
              Las dos juntas son lo que hace la diferencia entre <strong>comunicar</strong> y <strong>conducir</strong>.
            </p>
          </section>

          {/* ============ EL COSTO DE NO HACER NADA ============ */}
          <section className="pa-col pa-reveal">
            <div className="pa-cabecera">
              <div className="regla a"></div>
              <span className="eyebrow a">Lo que cuesta seguir igual</span>
              <h2>¿Cuántos vecinos te escribieron el mes pasado?</h2>
            </div>
            <p className="lede" style={{ marginTop: '1.2rem' }}>
              No lo sabemos. Y con todo respeto: vos tampoco, con precisión. Ese es exactamente el punto.
              No es que el equipo trabaje mal — es que sin un sistema atrás, todo lo que pasa se evapora
              a los pocos días. Esto es lo que se está yendo cada mes.
            </p>
          </section>

          <div className="pa-fugas pa-reveal">
            {FUGAS.map(([t, d]) => (
              <div className="pa-fuga" key={t}>
                <b>{t}</b>
                <span>{d}</span>
              </div>
            ))}
          </div>

          <div className="pa-col">
            <p className="pa-nota pa-reveal" style={{ marginTop: '1.2rem' }}>
              Nada de esto se arregla trabajando más horas: el equipo ya trabaja. Se arregla con algo que
              registre, ordene y avise — que es, en una frase, todo lo que sigue.
            </p>
          </div>

          {/* ============ BLOQUE 1 — HOY ============ */}
          <div className="pa-bloque pa-reveal">
            <span className="tag">Bloque 1 · Hoy</span>
            <p>
              Lo que arranca en el primer mes y resuelve el día a día de la banca.
              <strong> Todo esto es para el equipo que ya tenés</strong> — no hay que sumar gente
              ni rearmar nada.
            </p>
          </div>

          <section className="pa-col pa-reveal" style={{ marginTop: '1.8rem' }}>
            <div className="pa-cabecera">
              <div className="regla a"></div>
              <span className="eyebrow a">Servicios de Adbize</span>
              <h2>La parte que hacen personas</h2>
            </div>
            <p className="lede" style={{ marginTop: '1.1rem' }}>
              Trabajamos <strong>con tu equipo, no en lugar de tu equipo</strong>. Lo que hoy tu gente
              hace a pulmón — redactar una gacetilla, seguir qué publicaron los portales, anotar el
              reclamo de un vecino en el celular — pasa a tener un proceso detrás y alguien de
              nuestro lado responsable de que salga. Nadie pierde su rol: dejan de hacer el trabajo
              pesado y se quedan con el que solo ellos pueden hacer.
            </p>
          </section>

          <div className="pa-grid dos pa-reveal">
            <div className="pa-celda">
              <span className="eyebrow a">Prensa y vocería</span>
              <h3>Que lo que hacés salga publicado</h3>
              <p>Nos ocupamos de la relación con los medios locales y regionales, y de que tengas siempre algo listo para decir.</p>
              <ul>
                <li>Gacetillas y comunicados para cada proyecto y cada recorrida</li>
                <li>Agenda de contactos de prensa de San Nicolás y la región, con seguimiento de quién publicó qué</li>
                <li>Preparación antes de cada entrevista: los tres mensajes, las tres preguntas incómodas y cómo se responden</li>
                <li>Protocolo de crisis escrito: quién habla, qué se dice y en cuánto tiempo</li>
              </ul>
              <div className="pie">Entrega semanal + guardia ante urgencias</div>
            </div>

            <div className="pa-celda">
              <span className="eyebrow a">Pauta digital</span>
              <h3>Llegar al barrio que hace falta, no a todos</h3>
              <p>Campañas pagas en Meta y Google segmentadas por zona, edad e interés, con reporte de lo que realmente movió.</p>
              <ul>
                <li>Segmentación barrio por barrio, no “San Nicolás” entero</li>
                <li>Testeo de mensajes: qué frase funciona en qué zona, con números</li>
                <li>Empuje puntual detrás de un tema fuerte o una respuesta a un ataque</li>
                <li>Reporte mensual en lenguaje claro: cuánto costó llegar a cada persona y qué pasó después</li>
              </ul>
              <div className="pie">Presupuesto de pauta aparte, a tu criterio</div>
            </div>

            <div className="pa-celda">
              <span className="eyebrow a">Territorio y reclamos</span>
              <h3>Que ningún vecino se pierda</h3>
              <p>Desarrollamos y operamos el sistema donde entra, se ordena y se responde cada reclamo de barrio.</p>
              <ul>
                <li>Base única de vecinos: quién te escribió, por qué, y en qué quedó</li>
                <li>Mapa de San Nicolás con los reclamos ubicados, para ver dónde se concentra el problema</li>
                <li>Recorridas armadas con datos, no por intuición</li>
                <li>Aviso automático al vecino cuando su reclamo avanza — eso es lo que se recuerda</li>
              </ul>
              <div className="pie">Plataforma propia, desarrollada por nosotros</div>
            </div>

            <div className="pa-celda">
              <span className="eyebrow a">Contenido y presencia</span>
              <h3>Un ritmo que no se corta</h3>
              <p>Producción constante para que las redes acompañen la gestión en lugar de ir atrás.</p>
              <ul>
                <li>Calendario semanal de contenido atado a la agenda real del Concejo</li>
                <li>Piezas de la labor legislativa contadas en criollo, no en jerga de expediente</li>
                <li>Respuesta rápida: cuando aparece un tema, la pieza sale el mismo día</li>
                <li>Cobertura de recorridas y actos cuando hace falta</li>
              </ul>
              <div className="pie">Ritmo sostenido todo el año</div>
            </div>
          </div>

          <section className="pa-col pa-reveal">
            <div className="pa-cabecera">
              <div className="regla c"></div>
              <span className="eyebrow c">Plataforma Arcana</span>
              <h2>La parte que hace la máquina</h2>
            </div>
            <p className="lede" style={{ marginTop: '1.1rem' }}>
              Arcana es nuestra plataforma. Está construida, funcionando, y organizada en ocho equipos —
              los mismos equipos que tiene una operación política de verdad. La usa
              <strong> la gente que ya trabaja con vos</strong>: cada uno entra con su usuario y ve lo de su área,
              sin curso previo ni manual de cien páginas.
            </p>
            <p className="lede" style={{ marginTop: '1rem' }}>
              Y algo importante: <strong>Arcana se adapta a tu equipo, no al revés</strong>. Si tu gente
              trabaja de una manera y la pantalla no acompaña, cambiamos la pantalla. Como la construimos
              nosotros, ajustar un circuito, agregar un dato que ustedes necesitan o sacar algo que sobra
              es cuestión de días, no de negociarlo con un proveedor.
            </p>
          </section>

          <div className="pa-grid tres pa-reveal">
            <div className="pa-celda ia">
              <span className="eyebrow c">Conducción</span>
              <h3>La mesa chica</h3>
              <ul>
                <li>Resumen del día al abrir: qué pasó, qué mirar, qué decidir</li>
                <li>Copiloto que te devuelve borradores listos para usar, hablando o escribiendo</li>
                <li>Sala de crisis: arma el kit completo de respuesta en minutos</li>
                <li>Modo crisis: el sistema detecta el problema solo y abre el episodio antes de que te enteres por otro lado</li>
              </ul>
            </div>
            <div className="pa-celda ia">
              <span className="eyebrow c">Escucha</span>
              <h3>Qué se dice de vos</h3>
              <ul>
                <li>Monitoreo de redes, portales y comentarios, todos los días</li>
                <li>Termómetro de reputación: si sube o baja, y por qué</li>
                <li>Radar de oportunidades: temas que te conviene tomar antes que nadie</li>
                <li>Competencia 360°: qué comunican los otros y con qué resultado</li>
              </ul>
            </div>
            <div className="pa-celda ia">
              <span className="eyebrow c">Concejo</span>
              <h3>Seguimiento del HCD</h3>
              <ul>
                <li>Tu labor legislativa ordenada: proyectos, expedientes y estado de cada uno</li>
                <li>Seguimiento en vivo de las sesiones y de lo que presentan los demás</li>
                <li>Radar de licitaciones del municipio, con alerta cuando aparece algo raro</li>
              </ul>
            </div>
            <div className="pa-celda ia">
              <span className="eyebrow c">Territorio</span>
              <h3>La calle, en pantalla</h3>
              <ul>
                <li>Mapa de barrios con reclamos y recorridas</li>
                <li>Base de vecinos con historial de contacto</li>
                <li>Bot de Telegram: el equipo carga el reclamo desde el celular, en la esquina</li>
              </ul>
            </div>
            <div className="pa-celda ia">
              <span className="eyebrow c">Inteligencia</span>
              <h3>Saber más que el resto</h3>
              <ul>
                <li>Dossiers de personas y temas de la política local</li>
                <li>Investigación profunda de cualquier asunto, en minutos</li>
                <li>Tableros de análisis armados a medida</li>
              </ul>
            </div>
            <div className="pa-celda ia">
              <span className="eyebrow c">Datos</span>
              <h3>Los números</h3>
              <ul>
                <li>Mapa electoral por circuito y mesa</li>
                <li>Encuestas cargadas y comparadas</li>
                <li>Indicadores de campaña con metas y avance</li>
              </ul>
            </div>
          </div>

          <section className="pa-col pa-reveal" style={{ marginTop: '2.2rem' }}>
            <p className="lede">
              Y por encima de todo eso hay un <strong>agente</strong> al que le preguntás en castellano —
              “¿cómo venimos en el barrio Santa Rosa?”, “¿qué dijo la oposición esta semana?”, “armame el discurso de mañana” —
              y busca dentro de toda la plataforma antes de responder. No inventa: contesta con lo que hay cargado.
            </p>
          </section>

          {/* ============ ACCESOS POR PERSONA ============ */}
          <section className="pa-col pa-reveal">
            <div className="pa-cabecera">
              <div className="regla c"></div>
              <span className="eyebrow c">Accesos</span>
              <h2>Qué ve cada persona de tu equipo</h2>
            </div>
            <p className="lede" style={{ marginTop: '1.1rem' }}>
              Nadie entra a un sistema genérico donde ve todo mezclado. Cada uno de los que hoy trabajan
              con vos tiene su propio usuario y su propio permiso, y al abrir la plataforma se encuentra
              solo con lo suyo. Estos son los seis niveles, tal como están hoy en Arcana.
            </p>
          </section>

          <div className="pa-accesos-wrap pa-reveal">
            <table className="pa-accesos">
              <thead>
                <tr>
                  <th scope="col">Quién de tu equipo</th>
                  <th scope="col">Permiso</th>
                  <th scope="col">A qué entra</th>
                </tr>
              </thead>
              <tbody>
                {ACCESOS.map((a) => (
                  <tr key={a.rol}>
                    <td data-label="Quién de tu equipo"><span className="quien">{a.quien}<small>{a.sub}</small></span></td>
                    <td data-label="Permiso"><span className="rol">{a.rol}</span></td>
                    <td data-label="A qué entra">{a.que}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pa-col">
            <p className="pa-nota pa-reveal">
              Los permisos se cambian en cualquier momento desde Configuración, y los cambia Federico —
              no hay que pedirnos nada ni esperar. Las secciones más sensibles quedan cerradas por defecto:
              a los perfiles digitales solo llegan el administrador y el estratega.
            </p>
          </div>

          {/* ============ BLOQUE 2 — 2027 ============ */}
          <div className="pa-bloque pa-reveal">
            <span className="tag c">Bloque 2 · Camino a 2027</span>
            <p>Lo que construimos encima para que el trabajo de la banca se convierta en capital político.</p>
          </div>

          <section className="pa-col pa-reveal" style={{ marginTop: '1.8rem' }}>
            <div className="pa-cabecera">
              <div className="regla c"></div>
              <span className="eyebrow c">Productos a construir</span>
              <h2>Dieciocho herramientas que hoy no tiene nadie en San Nicolás</h2>
            </div>
            <p className="lede" style={{ marginTop: '1.1rem' }}>
              Todo lo anterior es para tu equipo. Esto es para afuera: plataformas que llevan tu nombre,
              que el vecino usa porque le sirven, y que instalan una idea antes de que empiece cualquier campaña —
              <strong> el que abre la información es él</strong>.
            </p>
            <p className="lede" style={{ marginTop: '1rem' }}>
              No se hacen todas, y menos todas juntas. Están agrupadas en cuatro familias según qué
              construye cada una. Elegís por dónde arrancar y vamos sumando de a una, en el orden que
              te sirva a vos. <strong>Hay una que va aparte</strong>, porque toca el tema que en San Nicolás
              define una elección — y es por la que nosotros empezaríamos.
            </p>
          </section>

          {/* ---- El destacado: la inseguridad es EL tema de San Nicolás ---- */}
          <div className="pa-destacado pa-reveal">
            <span className="eyebrow a">Empezaríamos por acá · El tema número uno</span>
            <div className="cabeza">
              <div className="nombre">Mapa del Delito de San Nicolás</div>
              <span className="quien">Público · con IA</span>
            </div>
            <p className="intro">
              El mapa de la inseguridad de la ciudad, armado con dos fuentes al mismo tiempo:
              lo que los vecinos ya publican en redes y lo que cualquiera puede marcar desde el celular.
              Se dibuja solo, en puntos de calor, y muestra dónde y a qué hora se roba en San Nicolás.
            </p>

            <div className="pa-como">
              {MAPA_PASOS.map((p, i) => (
                <div className="pa-paso" key={p.t}>
                  <span className="n">{i + 1}</span>
                  <b>{p.t}</b>
                  <span>{p.d}</span>
                </div>
              ))}
            </div>

            <div className="porque" style={{ marginTop: '1.6rem', borderLeft: '2px solid var(--pa-amber)', paddingLeft: '.9rem' }}>
              <b style={{ fontFamily: 'var(--pa-mono)', fontSize: '.63rem', letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--pa-amber)', display: 'block', marginBottom: '.3rem' }}>
                Por qué te sirve
              </b>
              <span style={{ color: 'var(--pa-ink-2)', fontSize: '.95rem' }}>
                La inseguridad es el tema que más preocupa en San Nicolás y hoy nadie tiene el dato: las cifras
                oficiales llegan tarde y la mayoría de los hechos ni siquiera se denuncian. El que tiene el mapa
                pasa a ser la fuente sobre el tema — cuando un medio quiera hablar de inseguridad, el número es
                tuyo. Y te da pedidos imposibles de contestar con humo: “en esta esquina hubo catorce hechos en
                dos meses, falta luz y falta patrullaje”. Además, cada vecino que marca un hecho es un contacto
                más, en el tema que más le importa.
              </span>
            </div>

            {/* --- La demo: que lo vea, no que se lo contemos --- */}
            <div className="pa-demo">
              <div className="barra">
                <span className="vivo"><i></i>Así se vería</span>
                <span className="pa-escala">
                  Menos <span className="rampa"></span> Más
                </span>
              </div>
              <div className="lienzo">
                <canvas ref={lienzo} aria-label="Mapa de calor de San Nicolás con los barrios reales y una muestra de hechos" role="img"></canvas>
              </div>
              <div className="patas">
                <div className="pata">
                  <div className="k">Barrios en el mapa</div>
                  <div className="v">{BARRIOS.length}</div>
                </div>
                {FOCOS_RESUMEN.slice(0, 3).map((f) => (
                  <div className="pata" key={f.etiqueta}>
                    <div className="k">{f.etiqueta}</div>
                    <div className="v">{f.hechos} hechos</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pa-aviso">
              <b>Qué estás viendo.</b> El mapa es San Nicolás de verdad: las calles, los accesos y el
              Paraná salen de OpenStreetMap, y los {BARRIOS.length} puntos blancos son los barrios que ya
              están cargados en la plataforma. <b>Los hechos, en cambio, son una muestra</b> — están
              puestos para que se entienda cómo se ve el mapa una vez que se llena; todavía no salimos a
              recolectar datos reales de delito. Por eso los focos dicen “sector norte” y no el nombre de
              un barrio: poner cifras inventadas sobre un barrio con nombre y apellido sería falso, y
              además injusto con la gente que vive ahí. Con datos reales, este mapa se pinta solo desde la
              primera semana.
            </div>

            <p className="pa-nota" style={{ marginTop: '1.3rem' }}>
              <strong>Cómo se cuida:</strong> se marca el hecho, nunca a una persona — sin nombres, sin fotos de
              gente y sin señalar sospechosos. Los reportes pasan por moderación para que nadie infle una zona a
              propósito, y el mapa aclara siempre que son reportes de vecinos y no estadística oficial. Eso es
              justamente lo que lo hace valioso: mide lo que la estadística oficial no ve.
            </p>
          </div>

          {FAMILIAS.map((f) => (
            <React.Fragment key={f.titulo}>
              <div className="pa-col pa-familia pa-reveal">
                <span className="eyebrow c">{f.eyebrow}</span>
                <div className="t">{f.titulo}</div>
                <p className="d">{f.desc}</p>
                <span className="cuenta">{f.productos.length} herramientas</span>
              </div>

              <div className="pa-fichas pa-reveal">
                {f.productos.map((p) => (
                  <article className="pa-ficha" key={p.nombre}>
                    <div className="top">
                      <div className="nombre">{p.nombre}</div>
                      <span className="quien">{p.quien}</span>
                    </div>
                    <p>{p.texto}</p>
                    <div className="porque">
                      <b>Por qué te sirve</b>
                      {p.porque}
                    </div>
                  </article>
                ))}
              </div>
            </React.Fragment>
          ))}

          <section className="pa-col pa-reveal">
            <div className="pa-cabecera">
              <div className="regla"></div>
              <span className="eyebrow">Y además</span>
              <h2>Ideas que se montan sobre lo mismo</h2>
            </div>
            <p className="lede" style={{ marginTop: '1rem' }}>
              Una vez que la base está armada, cada una de estas se construye en semanas, no en meses.
              Se activan cuando conviene, no todas juntas.
            </p>
          </section>

          <div className="pa-ideas pa-reveal">
            {IDEAS.map(([titulo, texto]) => (
              <div className="pa-idea" key={titulo}>
                <b>{titulo}</b>
                <span>{texto}</span>
              </div>
            ))}
          </div>

          {/* ============ CÓMO ARRANCAMOS ============ */}
          <section className="pa-col pa-reveal">
            <div className="pa-cabecera">
              <div className="regla a"></div>
              <span className="eyebrow a">Cómo arrancamos</span>
              <h2>Los primeros noventa días</h2>
            </div>
            <p className="lede" style={{ marginTop: '1rem' }}>
              No hace falta decidir todo hoy. Esta es la secuencia con la que empezaríamos.
            </p>
          </section>

          <div className="pa-etapas pa-reveal">
            <div className="pa-etapa">
              <div className="plazo"><span className="n num">1</span> Días 1 a 15</div>
              <h3>Puesta en marcha</h3>
              <p>
                Te damos acceso a la plataforma con tus datos ya cargados, conectamos la escucha de redes y
                medios, y armamos el sistema de reclamos con tu equipo. Arranca la prensa y sale la primera
                tanda de contenido. En paralelo ajustamos lo que tu gente pida.
              </p>
            </div>
            <div className="pa-etapa">
              <div className="plazo"><span className="n num">2</span> Días 30 a 45</div>
              <h3>Sale la primera plataforma pública</h3>
              <p>
                Lanzamos la primera herramienta pública — nuestra recomendación es el Mapa del Delito — con
                su propia campaña de presentación. Es rápido porque la base ya está construida: lo que se
                arma es la pieza pública, no el sistema de atrás. Ahí es donde te despegás del resto.
              </p>
            </div>
            <div className="pa-etapa">
              <div className="plazo"><span className="n num">3</span> Días 45 a 90</div>
              <h3>Ritmo propio y primeros números</h3>
              <p>
                Agenda de contenido semanal, reporte de prensa y la pauta corriendo segmentada por barrio.
                Ya se ve qué mensaje funciona en qué zona, y con eso definimos cuál es la segunda
                herramienta pública.
              </p>
            </div>
          </div>

          {/* ============ DE TU LADO ============ */}
          <section className="pa-col pa-reveal">
            <div className="pa-cabecera">
              <div className="regla"></div>
              <span className="eyebrow">De tu lado</span>
              <h2>Qué necesitamos para que esto funcione</h2>
            </div>
            <p className="lede" style={{ marginTop: '1rem' }}>
              Somos honestos con esto: la herramienta sola no gana nada. Necesita tres cosas del equipo.
            </p>
          </section>

          <div className="pa-pedido pa-reveal">
            <div>
              <span className="marca">Acceso</span>
              <h3>Una persona de referencia</h3>
              <p>Alguien de tu equipo que sea el contacto diario con nosotros y que pueda decir sí o no sin esperar una semana.</p>
            </div>
            <div>
              <span className="marca">Materia prima</span>
              <h3>Tu agenda real</h3>
              <p>Que lo que hacés nos llegue: recorridas, reuniones, proyectos. Sin eso comunicamos en el aire.</p>
            </div>
            <div>
              <span className="marca">Constancia</span>
              <h3>Cargar los reclamos</h3>
              <p>Que el equipo cargue lo que le llega del vecino, aunque sea desde el celular en la esquina. Es el hábito que sostiene todo lo demás.</p>
            </div>
          </div>

          {/* ============ EL PILOTO / RIESGO ============ */}
          <section className="pa-col pa-reveal">
            <div className="pa-cabecera">
              <div className="regla"></div>
              <span className="eyebrow">Sin riesgo de entrada</span>
              <h2>No te pedimos que apuestes: te pedimos que pruebes</h2>
            </div>
            <p className="lede" style={{ marginTop: '1rem' }}>
              Entendemos la desconfianza. Todo el mundo promete y después hay que aguantar el contrato.
              Por eso los primeros 90 días van <strong>a costo</strong>: cubrís lo que el servicio consume
              y nada más. Recién cuando puedas ver los resultados hablamos de un precio real.
            </p>
          </section>

          <div className="pa-garantias pa-reveal">
            {GARANTIAS.map(([t, d]) => (
              <div className="pa-garantia" key={t}>
                <span className="tick">Compromiso</span>
                <b>{t}</b>
                <span>{d}</span>
              </div>
            ))}
          </div>

          {/* ============ PROPIEDAD DE LOS DATOS ============ */}
          <section className="pa-col pa-reveal">
            <div className="pa-cabecera">
              <div className="regla c"></div>
              <span className="eyebrow c">La pregunta que importa</span>
              <h2>Los datos son tuyos. Sin asteriscos.</h2>
            </div>
            <p className="lede" style={{ marginTop: '1.1rem' }}>
              Es la duda que hay que sacarse de encima antes de empezar, porque lo más valioso que se
              construye acá no es la plataforma: es <strong>tu base de vecinos</strong>. Miles de personas
              con nombre, barrio, teléfono y el motivo por el que te buscaron. Eso no puede quedar en manos
              de un proveedor.
            </p>
          </section>

          <div className="pa-nosotros pa-reveal">
            <div>
              <h3>Son tuyos desde el día uno</h3>
              <p>
                Todo lo que se carga — vecinos, reclamos, contactos de prensa, contenidos — es tuyo, no
                nuestro. Nosotros lo operamos; el dueño sos vos.
              </p>
            </div>
            <div>
              <h3>Te los llevás cuando quieras</h3>
              <p>
                Pedís la exportación y te la entregamos completa, en formato abierto, sin trabas y sin
                tener que dar explicaciones. Junto con los datos van las herramientas que hayamos
                construido para vos. Arcana, la plataforma de fondo, queda de nuestro lado.
              </p>
            </div>
            <div>
              <h3>No se comparten ni se revenden</h3>
              <p>
                No se usan para otro cliente, no se cruzan con otras bases y no se venden a nadie. Y
                ningún otro cliente nuestro ve un solo dato tuyo.
              </p>
            </div>
            <div>
              <h3>Servidor propio, accesos por rol</h3>
              <p>
                Los datos viven en un servidor nuestro dedicado, no en una planilla que circula por
                WhatsApp. Cada persona ve solo su parte, y el acceso se corta en el momento si alguien
                deja el equipo.
              </p>
            </div>
          </div>

          {/* ============ QUIÉNES SOMOS ============ */}
          <section className="pa-col pa-reveal">
            <div className="pa-cabecera">
              <div className="regla a"></div>
              <span className="eyebrow a">Quiénes somos</span>
              <h2>Adbize</h2>
            </div>
            <p className="lede" style={{ marginTop: '1.1rem' }}>
              Somos un estudio de estrategia digital y desarrollo. La mejor prueba de lo que hacemos no es
              un portfolio: es que <strong>Arcana ya está funcionando en Lomas de Zamora y en Vicente
              López</strong>. No es un demo armado para esta reunión ni una plantilla comprada — la
              construimos nosotros, está en uso todos los días, y podés entrar a verla cuando quieras.
            </p>
          </section>

          <div className="pa-marca pa-reveal">
            <div className="pa-lockup">
              <img src="/img/adbize-logo.png" alt="Adbize" width="440" height="86" />
            </div>
            <div className="datos">
              <p className="tagline">
                Estrategia digital, desarrollo de software a medida e inteligencia artificial aplicada.
                Somos, además, <strong>de San Nicolás de los Arroyos</strong>: conocemos los barrios, los
                medios locales y a quién hay que llamar — y estamos a mano cuando hace falta.
              </p>
              <span className="donde">San Nicolás de los Arroyos · Buenos Aires</span>
              <div className="pa-enlaces">
                <a href="https://adbize.com" target="_blank" rel="noopener noreferrer">adbize.com</a>
                <a href="https://www.instagram.com/adbizedigital" target="_blank" rel="noopener noreferrer">Instagram · @adbizedigital</a>
                <a href="mailto:contacto@adbize.com">contacto@adbize.com</a>
              </div>
            </div>
          </div>

          <div className="pa-nosotros pa-reveal">
            <div>
              <h3>Ya está en la calle, no en un PowerPoint</h3>
              <p>
                La plataforma está funcionando hoy en <strong>Lomas de Zamora</strong> y en <strong>Vicente
                López</strong>, dos municipios del conurbano bonaerense. Todo lo que leíste acá ya pasó la
                prueba de un equipo usándolo en el día a día, no la de una demostración.
              </p>
            </div>
            <div>
              <h3>Lo construimos, no lo revendemos</h3>
              <p>
                La empezamos en agosto de 2024 y le venimos agregando cosas todas las semanas desde
                entonces: {BARRIOS.length} barrios mapeados, escucha de redes y medios, seguimiento del
                Concejo, agentes con IA. Cuando algo falta, lo programamos — no dependemos de que un
                proveedor lo agregue en su propio calendario.
              </p>
            </div>
            <div>
              <h3>Lo aprendido allá entra acá</h3>
              <p>
                Cada cosa que en el conurbano funcionó o falló ya está corregida en la plataforma que vas a
                usar vos. No arrancás de cero: arrancás desde donde llegaron los otros.
              </p>
            </div>
            <div>
              <h3>Trabajamos con una sola campaña por ciudad</h3>
              <p>
                No le vendemos lo mismo a tu competencia en San Nicolás. Es una decisión comercial y
                también de confianza: no se puede jugar en los dos equipos.
              </p>
            </div>
          </div>

          {/* ============ FAQ ============ */}
          <section className="pa-col pa-reveal">
            <div className="pa-cabecera">
              <div className="regla"></div>
              <span className="eyebrow">Preguntas incómodas</span>
              <h2>Lo que seguro te estás preguntando</h2>
            </div>
            <p className="lede" style={{ marginTop: '1rem' }}>
              Preferimos contestarlas acá y no que queden dando vueltas.
            </p>
          </section>

          <div className="pa-faq pa-reveal">
            {FAQ.map(([p, r]) => (
              <details key={p}>
                <summary>{p}</summary>
                <div className="r">{r}</div>
              </details>
            ))}
          </div>

          {/* ============ CIERRE ============ */}
          <div className="pa-cierre pa-reveal">
            <span className="eyebrow a">Lo que proponemos</span>
            <h2 style={{ marginTop: '1rem' }}>Empezar por lo que ya está construido, y armar lo demás juntos.</h2>
            <p>
              La plataforma existe y funciona hoy. Los servicios de prensa, pauta y contenido arrancan cuando digas.
              Las tres plataformas públicas las diseñamos con vos, en el orden que te sirva, y salen con tu nombre.
            </p>
            <p>
              La reunión que pedimos es de una hora: te mostramos Arcana funcionando en vivo, con datos de San Nicolás,
              y de ahí definimos por dónde empezar.
            </p>

            <div className="pa-contacto">
              <div className="item">
                <span className="k">Escribinos</span>
                <span className="v"><a href="mailto:contacto@adbize.com">contacto@adbize.com</a></span>
                <span className="n">Respondemos el mismo día.</span>
              </div>
              <div className="item">
                <span className="k">Web</span>
                <span className="v"><a href="https://adbize.com" target="_blank" rel="noopener noreferrer">adbize.com</a></span>
                <span className="n">Estrategia digital y desarrollo.</span>
              </div>
              <div className="item">
                <span className="k">Instagram</span>
                <span className="v"><a href="https://www.instagram.com/adbizedigital" target="_blank" rel="noopener noreferrer">@adbizedigital</a></span>
                <span className="n">Lo que vamos haciendo, al día.</span>
              </div>
              <div className="item">
                <span className="k">La plataforma</span>
                <span className="v"><a href="https://arcana.adbize.com" target="_blank" rel="noopener noreferrer">arcana.adbize.com</a></span>
                <span className="n">Arcana, funcionando hoy. El acceso te lo damos nosotros.</span>
              </div>
            </div>

            <div className="pie">
              <span><b>Adbize</b> — Estrategia digital y desarrollo</span>
              <span>Propuesta preparada para Federico Chouhy</span>
            </div>
          </div>

          <footer>
            <span>Adbize · contacto@adbize.com · adbize.com</span>
            <span>Documento de trabajo — no distribuir</span>
          </footer>

        </main>
      </div>
    </div>
  );
}
